import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import Divider from "../components/Divider";
import { Reveal } from "../components/Reveal";
import { Lightbox } from "../components/Lightbox";

const IMGS = {
  portrait: "/assets/stefano.jpg",
  tasting: "https://images.pexels.com/photos/8775456/pexels-photo-8775456.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  cellar: "https://images.pexels.com/photos/774454/pexels-photo-774454.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  vineyard: "https://images.unsplash.com/photo-1585553616435-2dc0a54e271d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHxfMTc4NzI2MDA2Nnw&q=85",
};

const TIMELINE = [
  { year: "1967", title: "Le origini", text: "Elio Cremonese inizia come agente di commercio: dal vino sfuso nelle trattorie veronesi al vino di qualità in bottiglia nei ristoranti." },
  { year: "1996", title: "La mia strada", text: "Inizio come agente di commercio vino e spirits nel canale Horeca. Verona è casa, da sempre." },
  { year: "2016", title: "La formazione", text: "Nascono i corsi: sedici edizioni del corso base, oltre 500 studenti, webinar e masterclass." },
  { year: "2026", title: "Dentro il Calice", text: "Il libro, il podcast, l'ecosistema: il progetto diventa un brand." },
];

export default function ChiSono() {
  const LB_IMAGES = [
    { src: "/assets/stefano.jpg", alt: "Stefano Cremonese, ritratto" },
    { src: "/assets/elio.jpg", alt: "Elio Cremonese, padre di Stefano, con un calice di vino rosso" },
    { src: "/assets/stefano-percorso-1.jpg", alt: "Stefano Cremonese in enoteca con una bottiglia di vino" },
    { src: "/assets/stefano-percorso-2.jpg", alt: "Stefano Cremonese al tavolo di un ristorante con un calice di vino" },
    { src: "/assets/stefano-studenti.jpg", alt: "Stefano Cremonese con due studenti durante una degustazione" },
  ];
  const [lbIndex, setLbIndex] = useState(null);
  return (
    <div data-testid="chi-sono-page">
      <SEO title="Chi sono — Stefano Cremonese" path="/chi-sono"
           description="Stefano Cremonese, Wine Influexpert: agente di commercio vino e spirits dal 1996, figlio d'arte, formatore con oltre 500 studenti." />

      <section className="bg-nero pt-40 pb-24 grain relative">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-14 items-center">
          <Reveal>
            <button type="button" onClick={() => setLbIndex(0)} data-testid="chi-foto-0" className="block mx-auto cursor-zoom-in group">
              <img src={IMGS.portrait} alt="Stefano Cremonese, ritratto" className="rounded-2xl w-72 aspect-[4/5] object-cover shadow-2xl border border-oro/20 transition-transform duration-500 group-hover:scale-[1.02]" loading="eager" />
            </button>
          </Reveal>
          <div>
            <Reveal delay={0.1}>
              <p className="text-oro text-xs uppercase tracking-[0.35em] mb-4">Chi sono</p>
              <h1 className="font-serif text-4xl sm:text-5xl font-bold text-panna leading-tight">
                Stefano Cremonese
              </h1>
              <p className="font-script text-3xl text-oro mt-4">Wine Influexpert</p>
              <p className="mt-6 text-panna/70 leading-relaxed max-w-xl">
                Trent'anni dentro le cantine e i ristoranti d'Italia. Non un esperto da manuale:
                uno che ha degustato in tutto il mondo accanto a vignaioli, agronomi e cantinieri —
                e poi ha portato quelle bottiglie sui tavoli.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-panna py-24">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 space-y-24">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14 items-center">
              <figure className="md:order-1">
                <button type="button" onClick={() => setLbIndex(1)} className="block w-full cursor-zoom-in group">
                  <img src="/assets/elio.jpg" data-testid="foto-elio"
                       alt="Elio Cremonese, padre di Stefano, con un calice di vino rosso"
                       className="rounded-2xl aspect-[4/5] object-cover object-top w-full shadow-xl border border-oro/15 transition-transform duration-500 group-hover:scale-[1.02]" loading="lazy" />
                </button>
                <figcaption className="mt-4 text-center font-serif italic text-nero/65 text-sm">
                  Mio padre Elio, al quale mi ispiro ogni giorno.
                </figcaption>
              </figure>
              <div className="md:order-2">
                <p className="text-oliva text-xs uppercase tracking-[0.35em] mb-4">Capitolo 01 — Le origini</p>
                <h2 className="font-serif text-3xl font-bold text-bordeaux">Figlio d'arte</h2>
                <p className="mt-6 text-nero/80 leading-relaxed text-base md:text-lg">
                  Mio padre Elio faceva l'agente di commercio dal 1967, quando il vino si vendeva sfuso nelle
                  trattorie veronesi e il vino in bottiglia nei ristoranti era una novità. Sono cresciuto con
                  l'odore delle cantine e il suono delle trattative. Verona è stata ed è casa mia: da qui partono
                  tutti i miei giri, e qui torno sempre.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setLbIndex(2)} className="cursor-zoom-in group">
                  <img src="/assets/stefano-percorso-1.jpg" data-testid="foto-percorso-1"
                       alt="Stefano Cremonese in enoteca con una bottiglia di vino"
                       className="rounded-xl aspect-[3/4] object-cover object-top shadow-lg w-full transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                </button>
                <button type="button" onClick={() => setLbIndex(3)} className="cursor-zoom-in group">
                  <img src="/assets/stefano-percorso-2.jpg" data-testid="foto-percorso-2"
                       alt="Stefano Cremonese al tavolo di un ristorante con un calice di vino"
                       className="rounded-xl aspect-[3/4] object-cover object-top shadow-lg w-full transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                </button>
              </div>
              <div>
                <p className="text-oliva text-xs uppercase tracking-[0.35em] mb-4">Capitolo 02 — Il percorso</p>
                <h2 className="font-serif text-3xl font-bold text-bordeaux">Dal 1996, sulla strada</h2>
                <p className="mt-6 text-nero/80 leading-relaxed">
                  Agente di commercio vino e spirits nel canale Horeca dal 1996. In mezzo: un'enoteca,
                  esperienze nell'import-export, un birrificio artigianale con brew pub e un format TV
                  enogastronomico. Ogni strada mi ha insegnato qualcosa che i manuali non dicono.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div className="md:order-2">
                <button type="button" onClick={() => setLbIndex(4)} className="block w-full cursor-zoom-in group">
                  <img src="/assets/stefano-studenti.jpg" data-testid="foto-studenti"
                       alt="Stefano Cremonese con due studenti durante una degustazione"
                       className="rounded-2xl aspect-[4/3] object-cover object-top shadow-lg w-full transition-transform duration-500 group-hover:scale-[1.02]" loading="lazy" />
                </button>
              </div>
              <div className="md:order-1">
                <p className="text-oliva text-xs uppercase tracking-[0.35em] mb-4">Capitolo 03 — La formazione</p>
                <h2 className="font-serif text-3xl font-bold text-bordeaux">Oltre 500 studenti</h2>
                <p className="mt-6 text-nero/80 leading-relaxed">
                  Sedici edizioni del corso base, webinar, masterclass. La cosa che mi riempie di più non è
                  il numero: è vedere persone che entrano timide davanti a un calice ed escono capaci di
                  scegliere, raccontare, godere. Senza snobismi, che è la regola numero uno.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal>
            <div>
              <p className="text-oliva text-xs uppercase tracking-[0.35em] mb-4">Capitolo 04 — Perché "Influexpert"</p>
              <h2 className="font-serif text-3xl font-bold text-bordeaux">Né influencer, né accademico</h2>
              <p className="mt-6 text-nero/80 leading-relaxed text-base md:text-lg">
                Non sono un tecnico da manuale. Sono uno che ha degustato in tutto il mondo accanto a
                vignaioli, agronomi e cantinieri, e che da trent'anni vende vino ai ristoranti. Influencer no:
                le foto non mi interessano. Esperto sì, ma di strada. Influexpert: uno che influenza perché sa,
                non perché posa.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-nero py-24 grain relative" data-testid="timeline-section">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna">La linea del tempo</h2>
            <Divider light className="mt-8" />
          </Reveal>
          <div className="relative">
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px bg-oro/30" />
            <div className="space-y-14">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.year} delay={i * 0.1}>
                  <div className={`relative flex ${i % 2 === 0 ? "sm:justify-start" : "sm:justify-end"}`}>
                    <div className="absolute left-4 sm:left-1/2 -translate-x-1/2 h-4 w-4 rounded-full border-2 border-oro bg-nero flex items-center justify-center">
                      <div className="h-1.5 w-1.5 rounded-full bg-oro" />
                    </div>
                    <div className={`ml-12 sm:ml-0 sm:w-[44%] ${i % 2 === 0 ? "" : ""}`}>
                      <p className="font-serif text-4xl font-bold text-oro">{t.year}</p>
                      <h3 className="font-serif text-xl font-semibold text-panna mt-2">{t.title}</h3>
                      <p className="mt-3 text-sm text-panna/65 leading-relaxed">{t.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="bg-bordeaux py-24 grain relative">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <p className="font-script text-3xl sm:text-4xl text-panna leading-snug">
              "Il vino è fatto dall'uomo, ognuno ha la sua storia personale, non è un prodotto standard.
              Stai bevendo storia, sacrificio e amore per la vite."
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/il-libro" data-testid="chi-sono-cta-libro"
                    className="inline-flex items-center gap-2 rounded-full bg-nero px-8 py-4 font-medium text-oro border border-oro hover:bg-nero-light transition-colors">
                Scopri il libro <ArrowRight size={16} />
              </Link>
              <Link to="/#guida-gratuita" data-testid="chi-sono-cta-guida"
                    className="inline-flex items-center gap-2 rounded-full border border-panna/50 px-8 py-4 font-medium text-panna hover:bg-panna/10 transition-colors">
                Scarica la guida gratuita
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Lightbox images={LB_IMAGES} index={lbIndex}
                onClose={() => setLbIndex(null)} onIndexChange={setLbIndex} />
    </div>
  );
}
